import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useSelectedView } from 'reducers/viewsSlice';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import 'styles/agGrid.css';

import { getPersistenceKey } from 'constants/helper';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';

import { useBulkActionContext } from '../ReviewManage/components/ReviewManageTable/components/BulkActions/BulkActionContext';
import { useOpenCalibrationViewSidepanel } from './CalibrationViewSidepanel/sidepanelStateHooks';
import { groupColumnDefinitionHeader } from './columnDefinitionHelpers';
import { getColumnDefinitionByType } from './columnDefinitions';
import { AGGRID_COLUMN_TYPES } from './dataHooks';
import { useCalibrationViewLandingQueryParams } from './helpers';

const initialVisibleColumnTypes = [
  AGGRID_COLUMN_TYPES.REVIEW_GRID_POSITION,
  AGGRID_COLUMN_TYPES.REVIEW_RATING,
  AGGRID_COLUMN_TYPES.REVIEW_OUTCOME,
  AGGRID_COLUMN_TYPES.REVIEW_OUTCOME_NEW,
  AGGRID_COLUMN_TYPES.REVIEW_STATUS,
  AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_RATING,
  AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_GRID_POSITION,
  AGGRID_COLUMN_TYPES.REVIEW_PREDICTED_PROMOTION_STATUS,
];
const initialVisibleColumnIds = ['EMPLOYEE'];

// for Talent Planning specific column definitions
export function getTPSpecificColumnDefinition(col) {
  let properties = {};

  // special condition for dynamic last comment column
  if (col.columnId?.startsWith('LAST_COMMENT')) {
    properties = {
      ...properties,
      filter: 'agTextColumnFilter',
    };
  }

  // eslint-disable-next-line default-case
  switch (col.columnId) {
    case AGGRID_COLUMN_TYPES.EMPLOYEE: {
      properties = {
        ...properties,
        pinned: 'left',
        lockPosition: 'left',
        cellClass: 'locked-col',
        suppressColumnsToolPanel: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionCurrentPageOnly: true,
        checkboxSelection: true,
        minWidth: 220,
      };
      break;
    }
  }

  // eslint-disable-next-line default-case
  if (
    !initialVisibleColumnTypes.includes(col.columnType) &&
    !initialVisibleColumnIds.includes(col.columnId) &&
    // special condition for dynamic comment column
    !col.columnId.startsWith('LAST_COMMENT')
  ) {
    properties = {
      ...properties,
      initialHide: true,
    };
  }

  return properties;
}

// export function getFeatureSpecificColumnDefinitionByType(col) {
//   switch (col.columnType) {
//     // because this is a group header for grouping purposes only and not a column

//     case AGGRID_COLUMN_TYPES.REVIEW_RATING: {
//       return {
//         suppressMovable: true,
//       };
//     }

//     case AGGRID_COLUMN_TYPES.REVIEW_OUTCOME: {
//       return {
//         suppressMovable: true,
//       };
//     }

//     case AGGRID_COLUMN_TYPES.REVIEW_GRID_POSITION: {
//       return {
//         suppressMovable: true,
//       };
//     }

//     default:
//       return {};
//   }
// }

// export function prepareTableData(cols, data) {
//   const tableRowData = {};

//   cols.forEach((col) => {
//     tableRowData[col.displayName] = data[col.id];
//   });

//   return tableRowData;
// }

function TalentPlanningAGGrid({ agGridRef, isFetching, reviewsManageData }) {
  const cohortDetails = useSelectedView();
  const { calibrationViewId: queryParamCalibrationViewId } = useCalibrationViewLandingQueryParams();
  const { selectedEmployeeIds, setSelectedEmployeeIds, setTotalEmployees } = useBulkActionContext();

  const { columns = [], values: rowsData } = reviewsManageData ?? {};

  const [columnDefs, setColumnDefs] = useState([]);

  const { openCalibrationSidepanel } = useOpenCalibrationViewSidepanel();

  useEffect(() => {
    if (isFetching) {
      agGridRef.current?.api?.setGridOption('loading', true);
    } else {
      agGridRef.current?.api?.hideOverlay();
    }
  }, [agGridRef, isFetching]);

  useEffect(() => {
    if (selectedEmployeeIds?.length === 0) {
      agGridRef.current?.api?.deselectAll();
    }
  }, [agGridRef, selectedEmployeeIds.length]);

  const onSelectionChanged = useCallback(
    (event) => {
      setSelectedEmployeeIds(event.api.getSelectedRows().map((e) => e.EMPLOYEE.id));
    },
    [setSelectedEmployeeIds]
  );

  const handleFilterAndDataChange = useCallback(() => {
    let total = 0;
    if (agGridRef.current?.api?.isAnyFilterPresent) {
      agGridRef.current?.api?.forEachNodeAfterFilter(() => {
        total += 1;
      });
    } else {
      total = rowsData?.length;
    }
    setTotalEmployees(total);
  }, [agGridRef, rowsData?.length, setTotalEmployees]);

  useEffect(() => {
    if (!columns || columns.length === 0) return;

    const newColumnDefs = columns.map((col) => {
      const colProps = col;
      // special condition for review rating column, where we use the ratingType from data values to decide on filters for the column: number of set
      if (col.columnType === AGGRID_COLUMN_TYPES.REVIEW_RATING) {
        // todo check how to decide on filter based on ratingType
      }
      return {
        colId: colProps.columnId,
        field: colProps.columnId,
        id: colProps.columnId,
        displayName: colProps.displayName,
        description: colProps.description,
        minWidth: 180,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          enableMenu: true,
          enableSorting: true,
        },
        headerValueGetter: () => colProps.displayName,

        // ...getFeatureSpecificColumnDefinitionByType(col),
        ...getColumnDefinitionByType(colProps),
        ...getTPSpecificColumnDefinition(colProps),
      };
    });

    const headerGroupedColumns = groupColumnDefinitionHeader(newColumnDefs, queryParamCalibrationViewId);
    setColumnDefs(headerGroupedColumns);
  }, [columns, queryParamCalibrationViewId]);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  function handleRowClick(data) {
    openCalibrationSidepanel({ cVPanelEmpId: data.EMPLOYEE.id, cVPanelCalibrationViewId: queryParamCalibrationViewId });
  }

  const gridStatePersistenceKey = useMemo(
    () =>
      getPersistenceKey({ moduleName: 'talentPlanning', viewId: cohortDetails?.id, uniqueId: 'TalentPlanningGrid' }),
    [cohortDetails?.id]
  );

  return (
    <Box className="review">
      <AgGridTable
        key={gridStatePersistenceKey} // we want to re-initialize on key change
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={agGridRef}
        rowData={rowsData}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        pagination
        suppressPaginationPanel={rowsData?.length < 6 || rowsData?.length === undefined || rowsData?.length === 0}
        paginationPageSize={10}
        sideBar={sideBar}
        headerHeight={36}
        autoHeight={false}
        rowHeight={56}
        suppressCellFocus
        onRowClicked={(e) => handleRowClick(e.data)}
        rowSelection="multiple"
        // using deprecated api, because new way has a bug, currently not working
        suppressRowClickSelection
        onSelectionChanged={onSelectionChanged}
        // onSelectionChanged={onSelectionChanged}
        // onRowDataUpdated={onRowDataUpdated}
        onFilterChanged={handleFilterAndDataChange}
        onFirstDataRendered={handleFilterAndDataChange}
        containerAutoHeight
        groupHeaderHeight={52}
        useColumnStatePersistence={false}
      />
    </Box>
  );
}

export { TalentPlanningAGGrid };
